import React, { useState } from 'react';
import { Container, Row, Col, Card, Badge, Button, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Calendar, Clock, Users, MapPin, BookOpen, User } from 'lucide-react';
import { useFetch } from '../hooks/useApiCall';
import { getCourseBySlug } from '../services/courseService';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import EnrollmentModal from '../components/EnrollmentModal';
import data from '../data/paomakeup';

const modalityLabels = {
  online: { label: 'Online', variant: 'info' },
  presencial: { label: 'Presencial', variant: 'success' },
  hybrid: { label: 'Hibrido', variant: 'warning' },
};

const formatPrice = (price, currency = 'DOP') => {
  if (price === 0 || price === '0') return 'Gratis';
  return new Intl.NumberFormat('es-DO', { style: 'currency', currency }).format(price);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const CourseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const { data: course, loading, error, refetch } = useFetch(getCourseBySlug, slug);

  if (loading) return <Container className="my-5"><LoadingSpinner /></Container>;
  if (error) return <Container className="my-5"><ErrorAlert message={error} onRetry={refetch} /></Container>;
  if (!course) return <Container className="my-5"><Alert variant="warning">Curso no encontrado.</Alert></Container>;

  const modality = modalityLabels[course.modality] || { label: course.modality, variant: 'secondary' };
  const sanitizedSyllabus = course.syllabus
    ? DOMPurify.sanitize(course.syllabus)
    : null;

  return (
    <>
      <Helmet>
        <title>{course.title} - {data.title}</title>
        <meta name="description" content={course.description ?? `Conoce nuestro curso: ${course.title}`} />
      </Helmet>

      {/* Hero Section */}
      <div className="course-detail-hero">
        {course.cover_image ? (
          <div
            className="course-detail-hero__bg"
            style={{ backgroundImage: `url(${course.cover_image})` }}
          />
        ) : (
          <div className="course-detail-hero__bg course-detail-hero__bg--gradient" />
        )}
        <div className="course-detail-hero__overlay" />
        <Container className="course-detail-hero__content">
          <Badge bg={modality.variant} className="mb-3">
            {modality.label}
          </Badge>
          <h1 className="course-detail-hero__title">{course.title}</h1>
          {course.instructor_name && (
            <p className="course-detail-hero__instructor">
              Instructor: {course.instructor_name}
            </p>
          )}
        </Container>
      </div>

      <Container className="my-5">
        <Button
          variant="outline-secondary"
          className="mb-4"
          onClick={() => navigate('/cursos')}
        >
          ← Volver a cursos
        </Button>

        <Row>
          {/* Main Content */}
          <Col lg={8}>
            {/* Description */}
            {course.description && (
              <section className="mb-5">
                <h2 className="course-detail-section-title">Descripcion del Curso</h2>
                <p className="course-detail-description">{course.description}</p>
              </section>
            )}

            {/* Syllabus */}
            {sanitizedSyllabus && (
              <section className="mb-5">
                <h2 className="course-detail-section-title">
                  <BookOpen size={20} className="me-2" />
                  Contenido del Curso
                </h2>
                <div
                  className="course-detail-syllabus"
                  dangerouslySetInnerHTML={{ __html: sanitizedSyllabus }}
                />
              </section>
            )}

            {/* Instructor */}
            {(course.instructor_name || course.instructor_bio) && (
              <section className="mb-5">
                <h2 className="course-detail-section-title">
                  <User size={20} className="me-2" />
                  Instructor
                </h2>
                <Card className="course-instructor-card">
                  <Card.Body>
                    <h5 className="mb-2">{course.instructor_name}</h5>
                    {course.instructor_bio && (
                      <p className="text-muted mb-0">{course.instructor_bio}</p>
                    )}
                  </Card.Body>
                </Card>
              </section>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <Card className="course-sidebar-card sticky-top" style={{ top: '1.5rem' }}>
              <Card.Body>
                <div className="course-sidebar-price mb-3">
                  {formatPrice(course.price)}
                </div>

                <div className="d-grid mb-4">
                  <Button variant="primary" size="lg" onClick={() => setShowModal(true)}>
                    Inscribirme
                  </Button>
                </div>

                <ul className="course-info-list list-unstyled">
                  {course.modality && (
                    <li className="course-info-list__item">
                      <MapPin size={16} className="course-info-list__icon" />
                      <span><strong>Modalidad:</strong> {modality.label}</span>
                    </li>
                  )}
                  {course.duration && (
                    <li className="course-info-list__item">
                      <Clock size={16} className="course-info-list__icon" />
                      <span><strong>Duracion:</strong> {course.duration}</span>
                    </li>
                  )}
                  {course.start_date && (
                    <li className="course-info-list__item">
                      <Calendar size={16} className="course-info-list__icon" />
                      <span><strong>Inicio:</strong> {formatDate(course.start_date)}</span>
                    </li>
                  )}
                  {course.end_date && (
                    <li className="course-info-list__item">
                      <Calendar size={16} className="course-info-list__icon" />
                      <span><strong>Finalizacion:</strong> {formatDate(course.end_date)}</span>
                    </li>
                  )}
                  {course.schedule && (
                    <li className="course-info-list__item">
                      <Clock size={16} className="course-info-list__icon" />
                      <span><strong>Horario:</strong> {course.schedule}</span>
                    </li>
                  )}
                  {course.capacity !== undefined && course.capacity !== null && (
                    <li className="course-info-list__item">
                      <Users size={16} className="course-info-list__icon" />
                      <span>
                        <strong>Cupos disponibles:</strong>{' '}
                        {course.remaining_capacity ?? course.capacity}
                      </span>
                    </li>
                  )}
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <EnrollmentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        course={course}
      />
    </>
  );
};

export default CourseDetail;
